// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Service for maintenance-related API calls

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function getAuthToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
}

export async function fetchPaidMaintenance(page = 1, limit = 16, filters = {}) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);

  if (filters.date) {
    queryParams.append('date', filters.date);
  }
  if (filters.projectId) {
    queryParams.append('project', filters.projectId);
  }
  if (filters.customerId) {
    queryParams.append('customer', filters.customerId);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/previous?${queryParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to fetch paid maintenance: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function fetchUnpaidMaintenance(page = 1, limit = 16, filters = {}) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);

  if (filters.date) {
    queryParams.append('date', filters.date);
  }
  if (filters.projectId) {
    queryParams.append('project', filters.projectId);
  }
  if (filters.customerId) {
    queryParams.append('customer', filters.customerId);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/remaining?${queryParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to fetch unpaid maintenance: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function closeMaintenance(maintenanceId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/close?maintenance_id=${maintenanceId}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to close maintenance: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function openMaintenance(maintenanceId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/open?maintenance_id=${maintenanceId}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to open maintenance: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function deleteMaintenance(maintenanceId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/delete/${maintenanceId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to delete maintenance: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function exportMaintenance(status) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/maintenance/export`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    throw new Error(`Failed to export maintenance: ${response.status}`);
  }

  // Handle file download
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `maintenance_${status}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
  
  return { success: true };
}